import { FLAGSHIP_ORG } from '@/lib/moduleAccess';

// ── Whose host is this? ──────────────────────────────────────────────────────
// The owner's rule (8 Oct 2026): the ONLY org on portal.parcelink.org is
// Parcelink, the operator of that portal. Every other business is a tenant
// and lives on its own Runmark host, or its own domain once it has one. The
// data layer never depended on the hostname (the org comes from the session),
// so this is about where people ARE, not what they can read: a tenant admin
// signed in on Parcelink's portal was still only ever shown their own org,
// but they were on Parcelink's address, wearing its URL, and that is wrong.
//
// A tenant's home is the shared app.runmark.co.uk until an address of their
// own ANSWERS: their <label>.runmark.co.uk once the org is active, or their
// verified custom domain. The VPS routes both itself (deploy/
// reconcile-domains.sh) and stamps orgs.subdomain_routed_at /
// custom_domain_routed_at only after the hostname served a page over https,
// so this file never sends anyone to an address that is not up yet.

const PARCELINK_SUFFIX = 'parcelink.org';
const RUNMARK_SUFFIX = 'runmark.co.uk';
export const FLAGSHIP_HOME = 'portal.parcelink.org';
export const TENANT_HOME = 'app.runmark.co.uk';
// The control plane (runmark.co.uk/control) is the platform's own console; it
// decides who may use it itself, so nobody is moved off it.
const CONTROL_PLANE = new Set(['runmark.co.uk', 'www.runmark.co.uk']);

const onParcelink = (h) => h === PARCELINK_SUFFIX || h.endsWith(`.${PARCELINK_SUFFIX}`);
const onRunmark = (h) => h === RUNMARK_SUFFIX || h.endsWith(`.${RUNMARK_SUFFIX}`);

/** Only the live hosts are bound; localhost and the -local.* dev hosts are not. */
export function isLiveHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (!h || h.endsWith('localhost') || h.includes('-local.')) return false;
  return onParcelink(h) || onRunmark(h);
}

// A developer's box: localhost, the -local.* names, .local/.test, a bare IP.
const DEV_HOST = /(^|\.)localhost$|-local\.|\.local$|\.test$|^\d+\.\d+\.\d+\.\d+$|^\[/;

/**
 * An address reached over PLAIN http that the VPS has not routed yet: a
 * tenant's <label>.runmark.co.uk in the minute or two between the org going
 * active and its router and certificate, or a custom domain in the same
 * window. Traefik's catch-all on port 80 (deploy/compose-prod.yml,
 * portal-switching-on) hands such a request to the portal, which shows the
 * holding screen (SwitchingOnPage) instead of a certificate warning and a
 * bare 404. Null for the shared host, the control plane, the apex, every
 * Parcelink host (named in Traefik; one here goes to https) and a dev host.
 */
export function unroutedTenantHost({ protocol, hostname } = {}) {
  if (protocol !== 'http:') return null;
  const h = String(hostname || '').toLowerCase();
  if (!h || DEV_HOST.test(h)) return null;
  if (onRunmark(h)) return h === RUNMARK_SUFFIX || h === TENANT_HOME || CONTROL_PLANE.has(h) ? null : h;
  if (onParcelink(h)) return null;
  return h;
}

/**
 * The https address of a live host reached over plain http, or null to stay.
 * Traefik redirects the named hosts itself; this covers one that reached the
 * app through the catch-all, and never a dev host, which has no certificate
 * to go to, nor an unrouted address, which has none yet.
 *
 * Nor an address with an explicit port. Real plain-http traffic arrives on
 * Traefik's :80 catch-all with an empty `location.port`; a port means a local
 * server such as the build's prerender, which serves the live hostnames at
 * http://<host>:8799 and must be left to render. From 19 to 26 Oct 2026 this
 * upgrade sent every prerendered page to https://<host>/ (127.0.0.1:443 in
 * the build, nothing listening), so the build shipped no snapshots at all.
 */
export function httpsUrl({ protocol, hostname, port = '', pathname = '/', search = '' } = {}) {
  if (protocol !== 'http:') return null;
  if (String(port || '') !== '') return null;
  const h = String(hostname || '').toLowerCase();
  if (!isLiveHost(h) || unroutedTenantHost({ protocol, hostname })) return null;
  return `https://${h}${pathname || '/'}${search || ''}`;
}

/**
 * Where an org's people belong. An address counts once the VPS has reported
 * it serving (`*_routed_at`); `routedHosts` is kept for callers that know
 * better (tests, a one-off).
 */
export function homeHost(org, { routedHosts = [] } = {}) {
  if (!org) return null;
  if (org.org_key === FLAGSHIP_ORG) return FLAGSHIP_HOME;
  const custom = org.custom_domain_status === 'verified' ? String(org.custom_domain || '').toLowerCase() : '';
  if (custom && (org.custom_domain_routed_at || routedHosts.includes(custom))) return custom;
  const vanity = org.subdomain ? `${String(org.subdomain).toLowerCase()}.${RUNMARK_SUFFIX}` : '';
  if (vanity && (org.subdomain_routed_at || routedHosts.includes(vanity))) return vanity;
  return TENANT_HOME;
}

/**
 * The address to send a signed-in person to on another host, with their
 * session carried across. Browser storage is per origin, so a plain redirect
 * to the tenant's own domain arrives signed out; the tokens travel in the URL
 * fragment, which the Supabase client reads on arrival and clears (the same
 * implicit-flow landing a magic link uses), and which is never sent to a
 * server. No session, or a session without a refresh token: a plain URL.
 */
export function handoffUrl(target, { pathname = '/', search = '' } = {}, session = null) {
  const base = `https://${target}${pathname || '/'}${search || ''}`;
  if (!session?.access_token || !session?.refresh_token) return base;
  const now = Math.floor(Date.now() / 1000);
  const expiresIn = Math.max(60, Number(session.expires_at || 0) - now) || 3600;
  const hash = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_in: String(expiresIn),
    token_type: 'bearer',
  });
  return `${base}#${hash.toString()}`;
}

/**
 * The host to move a signed-in person to, or null to stay. Parcelink's people
 * leave Runmark's tenant hosts; a tenant's people leave every Parcelink host
 * and, when they have a routed home of their own, the shared tenant host.
 */
export function hostRedirect(hostname, org, opts = {}) {
  const h = String(hostname || '').toLowerCase();
  if (!org || !isLiveHost(h) || CONTROL_PLANE.has(h)) return null;
  const home = homeHost(org, opts);
  if (org.org_key === FLAGSHIP_ORG) return onParcelink(h) ? null : home;
  if (onParcelink(h)) return home;
  if (home !== TENANT_HOME && h !== home) return home;
  return null;
}
